import { useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney } from "../engine/dates";
import { clearQuoteOverride, saveQuoteOverride, saveQuoteRule } from "../lib/data";

/**
 * The pipeline, in Scoro's own language.
 *
 * Stages, names and colours all come from Scoro, so a stage renamed there
 * renames here. Inventing a parallel set of stages would mean two vocabularies
 * for the same pipeline and an argument about which is right.
 *
 * Rejected, expired and completed quotes are never synced: a completed quote has
 * become an invoice and is already in the forecast, so showing it here would
 * count the same work twice.
 */

/** Scoro's stage colours are backgrounds; pick readable ink for each. */
function inkFor(hex) {
  if (!hex) return T.ink;
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  if ([r, g, b].some(isNaN)) return T.ink;
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#1A1815" : "#FFFFFF";
}

function StageChip({ label, colour }) {
  return (
    <span
      style={{
        background: colour || T.borderSoft,
        color: inkFor(colour),
        borderRadius: 999,
        padding: "2px 9px",
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

/**
 * The stages where the work is settled enough to be worth forecasting.
 *
 * Matched on the label rather than Scoro's `additional9`-style codes, which are
 * positions in a list and get renumbered underneath us.
 */
const SETTLED = /^\s*(planned income|po needed|invoice ready)/i;

export default function QuotesView({ quotes, statuses, rules, overrides = {}, onOpenQuote, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");

  const { groups, hiddenCount, hiddenValue } = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Account managers quote a project number as often as a quote number, so
    // both are searchable, along with the names either might be filed under.
    const matches = (x) =>
      !q ||
      [x.no, x.project_id, x.company_name, x.quote_name, x.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));

    const settled = (x) => SETTLED.test(statuses[x.status]?.label || "");
    const visible = quotes.filter((x) => matches(x) && (showAll || q ? true : settled(x)));
    const hidden = quotes.filter((x) => !settled(x));

    const by = {};
    for (const x of visible) {
      (by[x.status] = by[x.status] || []).push(x);
    }
    const built = Object.entries(by)
      .map(([status, items]) => ({
        status,
        label: statuses[status]?.label || status,
        colour: statuses[status]?.colour,
        items: items.sort((a, b) =>
          String(a.estimated_closing_date || a.deadline || "").localeCompare(
            String(b.estimated_closing_date || b.deadline || "")
          )
        ),
        gross: items.reduce((s, q) => s + (Number(q.sum) || 0) + (Number(q.vat_sum) || 0), 0),
        rule: rules[status] || { include: false, probability: 100 },
      }))
      .sort((a, b) => b.gross - a.gross);
    return {
      groups: built,
      hiddenCount: hidden.length,
      hiddenValue: hidden.reduce((s2, x) => s2 + (Number(x.sum) || 0) + (Number(x.vat_sum) || 0), 0),
    };
  }, [quotes, statuses, rules, showAll, query]);

  /**
   * One quote in or out, without touching its stage.
   *
   * The stage toggle is all-or-nothing — switching on "PO Needed" pulls in all
   * sixteen — and there is always a subset you actually believe. Clearing the
   * override rather than storing "excluded" hands the quote back to its stage,
   * so it is not silently stuck out if the stage is later switched on.
   */
  const toggleQuote = async (q, wantIn) => {
    setBusy(q.id);
    setError(null);
    try {
      const stageOn = !!rules[q.status]?.include;
      if (wantIn === stageOn) await clearQuoteOverride(q.id);
      else await saveQuoteOverride(q.id, { include: wantIn, probability: rules[q.status]?.probability ?? 100 });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const update = async (status, include, probability, repeatMonthly) => {
    setBusy(status);
    setError(null);
    try {
      await saveQuoteRule(status, include, probability, repeatMonthly);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const totalIncluded = groups
    .filter((g) => g.rule.include)
    .reduce((s, g) => s + g.gross * (Number(g.rule.probability) / 100), 0);

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Pipeline</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 680 }}>
        Quotes that are settled enough to be worth forecasting — planned income, PO needed, invoice
        ready — in Scoro's own stages and colours. Switch a stage on to put it
        in the cash forecast, and set how much of it to believe. Nothing is included by default —
        a quote is not cash, and it shouldn't appear in a forecast because a default said so.
        Quotes land on their estimated closing date where Scoro has one. A stage switch is
        all-or-nothing; to add one quote, open it — you can set when it should land and attach the
        cost of delivering it before it goes anywhere near the forecast.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "14px 0" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Quote or project number, client, description…"
          style={{
            fontFamily: "inherit",
            fontSize: 12,
            padding: "7px 10px",
            width: 320,
            borderRadius: 6,
            border: `1px solid ${T.border}`,
          }}
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
          >
            clear
          </button>
        )}
        {hiddenCount > 0 && !query && (
          <label style={{ fontSize: 11, color: T.muted, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            show the earlier stages too — {hiddenCount} quotes, {fmtMoney(hiddenValue)}
          </label>
        )}
        {query && (
          <span style={{ fontSize: 11, color: T.muted }}>
            searching every stage
          </span>
        )}
      </div>

      <div style={{ margin: "14px 0", fontSize: 13 }}>
        Currently feeding the forecast: <strong>{fmtMoney(totalIncluded)}</strong>
        <span style={{ color: T.muted }}>
          {" "}
          of {fmtMoney(groups.reduce((s, g) => s + g.gross, 0))} total pipeline
        </span>
      </div>

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 10 }}>{error}</div>}

      {groups.map((g) => (
        <div
          key={g.status}
          style={{
            background: T.panel,
            borderRadius: 10,
            marginBottom: 12,
            border: `1px solid ${g.rule.include ? T.accent : T.borderSoft}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              borderBottom: `1px solid ${T.borderSoft}`,
              flexWrap: "wrap",
            }}
          >
            <StageChip label={g.label} colour={g.colour} />
            <span style={{ fontSize: 12, color: T.muted }}>
              {g.items.length} quote{g.items.length === 1 ? "" : "s"}
            </span>
            <span style={{ fontFamily: fontMono, fontSize: 13 }}>{fmtMoney(g.gross)}</span>
            {g.rule.include && g.rule.repeat_monthly && (
              <span style={{ fontSize: 10, color: T.accent, fontWeight: 600 }}>
                ↻ every month
              </span>
            )}

            <label style={{ marginLeft: "auto", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={!!g.rule.include}
                disabled={busy === g.status}
                onChange={(e) =>
                  update(g.status, e.target.checked, g.rule.probability ?? 100, g.rule.repeat_monthly)
                }
              />
              in forecast
            </label>
            {g.rule.repeat_monthly && !g.rule.include && (
              <span style={{ fontSize: 10, color: T.warn }}>
                repeats, but the stage is off — nothing shows
              </span>
            )}
            <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
              at
              <input
                type="number"
                min="0"
                max="100"
                defaultValue={g.rule.probability ?? 100}
                disabled={busy === g.status}
                onBlur={(e) => {
                  const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                  if (v !== Number(g.rule.probability ?? 100))
                    update(g.status, g.rule.include, v, g.rule.repeat_monthly);
                }}
                style={{ width: 52, fontFamily: fontMono, fontSize: 11, padding: "2px 4px" }}
              />
              %
            </label>
            {/* Retainers recur by nature; most stages do not. */}
            <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
              <input
                type="checkbox"
                checked={!!g.rule.repeat_monthly}
                disabled={busy === g.status}
                onChange={(e) =>
                  // Switching on "repeats monthly" for a stage that isn't in the
                  // forecast would do nothing at all, silently. Nobody ticks
                  // repeat to keep a stage excluded, so it turns the stage on.
                  update(g.status, e.target.checked ? true : g.rule.include, g.rule.probability ?? 100, e.target.checked)
                }
              />
              repeats monthly
            </label>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <tbody>
              {g.items.map((q) => (
                <tr
                  key={q.id}
                  onClick={() => onOpenQuote?.(q)}
                  style={{ borderTop: `1px solid ${T.borderSoft}`, cursor: "pointer" }}
                  title="Open this quote"
                >
                  <td style={{ padding: "6px 8px 6px 14px", width: 96 }}>
                    {/* A status, not a switch. Adding a quote to a cash forecast
                        deserves a look at when it lands and what it costs, so
                        this opens the quote rather than committing it. */}
                    {(overrides[q.id] ? overrides[q.id].include : rules[q.status]?.include) ? (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: T.income,
                          background: "#EDF3ED",
                          border: `1px solid ${T.income}`,
                          borderRadius: 999,
                          padding: "2px 7px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ✓ IN FORECAST
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: T.muted,
                          border: `1px solid ${T.border}`,
                          borderRadius: 999,
                          padding: "2px 7px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        review →
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "6px 8px", fontFamily: fontMono, color: T.muted, width: 62 }}>
                    {q.no}
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <div>
                      {q.company_name || "—"}
                      {overrides[q.id] && (
                        <span style={{ fontSize: 9, color: T.accent, marginLeft: 6 }}>
                          {overrides[q.id].include ? "· ADDED BY HAND" : "· HELD OUT"}
                        </span>
                      )}
                    </div>
                    {(q.description || q.quote_name) && (
                      <div style={{ fontSize: 10, color: T.muted }}>
                        {q.description || q.quote_name}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "6px 8px", color: T.muted, whiteSpace: "nowrap" }}>
                    {q.estimated_closing_date ? (
                      <>
                        closes {fmtDay(q.estimated_closing_date)}
                      </>
                    ) : (
                      <span style={{ color: T.warn }}>no closing date — uses {fmtDay(q.deadline)}</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "6px 14px",
                      textAlign: "right",
                      fontFamily: fontMono,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtMoney((Number(q.sum) || 0) + (Number(q.vat_sum) || 0))}
                    {g.rule.include && Number(g.rule.probability) !== 100 && (
                      <div style={{ fontSize: 9, color: T.accent }}>
                        {fmtMoney(
                          ((Number(q.sum) || 0) + (Number(q.vat_sum) || 0)) *
                            (Number(g.rule.probability) / 100)
                        )}{" "}
                        in forecast
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {!groups.length && (
        <div style={{ fontSize: 12, color: T.muted }}>
          No open quotes in Scoro. Rejected, expired and completed ones are deliberately not synced.
        </div>
      )}
    </div>
  );
}
